class sv_UIComponent {
  constructor({
    triggerSelector,
    containerSelector,
    closeSelector,
    options = {},
  }) {
    // --- ERROR HANDLING FOR MISSING OR INVALID SELECTORS ---
    if (!containerSelector) {
      console.error('[sv_UIComponent] containerSelector is required.');
      return;
    }
    this.container = document.querySelector(containerSelector);
    if (!this.container) {
      console.error(
        `[sv_UIComponent] No element found for containerSelector "${containerSelector}".`
      );
      return;
    }

    if (triggerSelector) {
      this.triggerEls = Array.from(
        document.querySelectorAll(triggerSelector)
      );
      if (this.triggerEls.length === 0) {
        console.warn(
          `[sv_UIComponent] triggerSelector "${triggerSelector}" did not match any elements.`
        );
      }
    } else {
      // triggerSelector optional
      this.triggerEls = [];
    }

    if (closeSelector) {
      this.closeEls = Array.from(
        this.container.querySelectorAll(closeSelector)
      );
      if (this.closeEls.length === 0) {
        console.warn(
          `[sv_UIComponent] closeSelector "${closeSelector}" did not match any elements inside container.`
        );
      }
    } else {
      this.closeEls = [];
    }

    this.opts = Object.assign({ duration: 300 }, options);
    this.isOpen = false;

    if (!this.container.id) {
      console.warn(
        '[sv_UIComponent] container element has no id; ARIA attributes may not work properly.'
      );
    }

    // Initial ARIA setup
    this.container.setAttribute("aria-hidden", "true");
    this.triggerEls.forEach((btn) => {
      try {
        btn.setAttribute("aria-controls", this.container.id);
        btn.setAttribute("aria-expanded", "false");
      } catch (err) {
        console.error(
          `[sv_UIComponent] Failed to set ARIA attributes on trigger:`,
          err
        );
      }
    });

    this._bindTriggers();
    this._bindClosers();
  }

  _bindTriggers() {
    this.triggerEls.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        this.open();
      });
    });
  }

  _bindClosers() {
    this.closeEls.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        this.close();
      });
    });
  }

  open() {
    try {
      // remove inert if used
      if ("inert" in this.container) this.container.inert = false;

      this.container.style.setProperty(
        "--sv-duration",
        this.opts.duration + "ms"
      );
      this.container.classList.add("sv_animating");
      requestAnimationFrame(() => {
        this.container.classList.add("sv_is-active");
        const first = this.container.querySelector(
          this.focusableSelectors || "button, a, input, [tabindex]"
        );
        if (first) first.focus();
      });
      this.container.addEventListener(
        "transitionend",
        () => this.container.classList.remove("sv_animating"),
        { once: true }
      );
      this.container.setAttribute("aria-hidden", "false");
      this.triggerEls.forEach((btn) =>
        btn.setAttribute("aria-expanded", "true")
      );
      this.isOpen = true;
    } catch (err) {
      console.error("[sv_UIComponent] Error during open():", err);
    }
  }

  close() {
    try {
      // 1) move focus off anything inside
      const active = document.activeElement;
      if (active && this.container.contains(active)) {
        if (this.triggerEls.length) {
          this.triggerEls[0].focus();
        } else {
          active.blur();
        }
      }

      // 2) optional inert
      if ("inert" in this.container) this.container.inert = true;

      // 3) animation + hide
      this.container.style.setProperty(
        "--sv-duration",
        this.opts.duration + "ms"
      );
      this.container.classList.add("sv_animating");
      requestAnimationFrame(() => {
        this.container.classList.remove("sv_is-active");
      });
      this.container.addEventListener(
        "transitionend",
        () => this.container.classList.remove("sv_animating"),
        { once: true }
      );
      this.container.setAttribute("aria-hidden", "true");
      this.triggerEls.forEach((btn) =>
        btn.setAttribute("aria-expanded", "false")
      );
      this.isOpen = false;
    } catch (err) {
      console.error("[sv_UIComponent] Error during close():", err);
    }
  }
}

class sv_OverlayComponent extends sv_UIComponent {
  constructor(config) {
    super(config);

    if (!this.triggerEls.length) {
      console.error(
        '[sv_OverlayComponent] triggerSelector is required for overlays and must match at least one element.'
      );
    }

    this.focusableSelectors =
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
    this._onKeydown = this._onKeydown.bind(this);
    document.addEventListener("keydown", this._onKeydown);

    if (this.opts.clickOutside) {
      document.addEventListener("click", (e) => {
        if (
          this.isOpen &&
          !this.container.contains(e.target) &&
          !this.triggerEls.some((btn) => btn.contains(e.target))
        ) {
          this.close();
        }
      });
    }
  }

  _onKeydown(e) {
    if (!this.isOpen) return;
    if (e.key === "Escape") {
      this.close();
      return;
    }
    if (e.key === "Tab" && this.opts.trapFocus) {
      const focusable = Array.from(
        this.container.querySelectorAll(this.focusableSelectors)
      );
      if (!focusable.length) return;
      const first = focusable[0],
        last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }
}

class sv_NotificationComponent extends sv_UIComponent {
  constructor(config) {
    super({
      ...config,
      triggerSelector: config.triggerSelector || undefined,
    });

    this.storageKey = this.opts.storageKey || "notification-dismissed";
    let dismissed;
    try {
      dismissed = Boolean(localStorage.getItem(this.storageKey));
    } catch (err) {
      console.warn(
        "[sv_NotificationComponent] Could not access localStorage:",
        err
      );
      dismissed = true;
    }

    if (!dismissed) {
      this.open();
    }
  }

  close() {
    super.close();
    try {
      localStorage.setItem(this.storageKey, "true");
    } catch (err) {
      console.warn(
        "[sv_NotificationComponent] Could not write to localStorage:",
        err
      );
    }
  }
}

export { sv_UIComponent, sv_OverlayComponent, sv_NotificationComponent };